import PrivateImage from '../presentation/PrivateImage';
import React, {useEffect, useRef, useState} from "react";
import {Redirect, useParams} from "react-router-dom";
import {Button, Divider, Grid, Toolbar} from "@material-ui/core";
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";
import {makeStyles} from "@material-ui/core/styles";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import {PDFExport} from "@progress/kendo-react-pdf";
import {NotificationManager} from "react-notifications";
import UserService from "../../services/user.service";
import {logOut} from "../../helpers/logout";
import {getPdfExportProxyProps, savePdfWithEmbeddedImages} from "../../helpers/pdfExport";
import ActionButton from "../form/input/ActionButton";
import GeneratedDocumentEmailAction from "./GeneratedDocumentEmailAction";
import "../../Print.css";

const useStyles = makeStyles((theme) => ({
    root: {flexGrow: 1, width: "100%", backgroundColor: theme.palette.background.paper},
    seeMore: {marginTop: theme.spacing(8)},
    paper: {padding: theme.spacing(2), display: "flex", overflow: "auto", flexDirection: "column"},
    marginTop: {marginTop: theme.spacing(2)},
    editHint: {margin: "0 0 12px", fontWeight: 700, textAlign: "center", color: "#2c4767"},
    pages: {background: "transparent"},
    page: {
        boxSizing: "border-box",
        width: "210mm",
        height: "297mm",
        padding: "12mm 11mm 10mm",
        overflow: "hidden",
        background: "#fffefb",
        color: "#1f2b3a",
        fontFamily: '"DejaVu Sans", "Arial", sans-serif',
        fontSize: 10,
        lineHeight: 1.25,
    },
    secondPage: {pageBreakBefore: "always"},
    header: {display: "grid", gridTemplateColumns: "28mm 1fr 42mm", gap: "5mm", alignItems: "center", marginBottom: "5mm"},
    logo: {maxWidth: "27mm", maxHeight: "18mm", objectFit: "contain"},
    logoPlaceholder: {height: "17mm", border: "1px dashed #c8d3df", display: "flex", alignItems: "center", justifyContent: "center", color: "#8190a3", textAlign: "center", fontSize: 8},
    title: {margin: 0, textAlign: "center", color: "#203f67", fontSize: 19, lineHeight: 1.2, letterSpacing: ".02em"},
    languages: {marginTop: 2, textAlign: "center", color: "#60728a", fontSize: 7.5, fontStyle: "italic"},
    metaBox: {padding: "2.5mm", border: "1px solid #c8d3df", borderRadius: 4, background: "#f7f9fb"},
    metaRow: {display: "flex", alignItems: "center", gap: 4, marginBottom: 3, fontWeight: 700},
    section: {marginBottom: "3.5mm", border: "1px solid #203f67", borderRadius: 4, overflow: "hidden"},
    sectionHeader: {padding: "2mm 2.5mm", background: "#203f67", color: "#fffefb"},
    sectionTitle: {fontSize: 11, fontWeight: 700},
    sectionTranslation: {marginTop: 1, fontSize: 7.3, fontStyle: "italic", color: "#dce5ef"},
    sectionBody: {display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "2.5mm 3mm", padding: "3mm"},
    threeColumns: {gridTemplateColumns: "repeat(3, minmax(0, 1fr))"},
    full: {gridColumn: "1 / -1"},
    fieldLabel: {fontSize: 9.5, fontWeight: 700, color: "#26384e"},
    fieldTranslation: {minHeight: 18, marginTop: 1, color: "#71829a", fontSize: 6.9, lineHeight: 1.15},
    input: {boxSizing: "border-box", width: "100%", height: 24, marginTop: 3, padding: "2px 5px", overflow: "hidden", border: "1px solid #c8d3df", borderRadius: 3, background: "#f8fafc", color: "#17283b", fontFamily: '"DejaVu Sans", "Arial", sans-serif', fontSize: 9.5, fontWeight: 600},
    textarea: {height: 42, resize: "none"},
    routeGrid: {display: "grid", gridTemplateColumns: "1fr 27mm", gap: "3mm"},
    qr: {display: "flex", alignItems: "center", justifyContent: "center", height: "29mm", border: "1px dashed #c8d3df", color: "#8190a3", textAlign: "center", fontSize: 8},
    signatures: {display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8mm", marginTop: "5mm"},
    signature: {height: "35mm", padding: "3mm", border: "1px solid #c8d3df", background: "#f8fafc", display: "flex", flexDirection: "column", justifyContent: "flex-end", textAlign: "center"},
    signatureLine: {borderTop: "1px dashed #91a0b2", paddingTop: 4, color: "#203f67", fontWeight: 700},
    pageNumber: {marginTop: "auto", textAlign: "right", color: "#71829a", fontSize: 8},
}));

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("sk-SK", {day: "2-digit", month: "2-digit", year: "numeric"});
};

const formatTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleTimeString("sk-SK", {hour: "2-digit", minute: "2-digit"});
};

const initialFields = {
    number: "",
    documentDate: formatDate(new Date()),
    companyName: "",
    manager: "",
    companyAddress: "",
    companyIds: "",
    companyPhone: "",
    companyEmail: "",
    driverOne: "",
    driverTwo: "",
    vehicleModel: "",
    vehiclePlate: "",
    vehicleCountry: "",
    deceased: "",
    deceasedId: "",
    birthDate: "",
    birthPlace: "",
    deathDate: "",
    deathPlace: "",
    institution: "",
    institutionAddress: "",
    routeUrl: "",
    routeNotes: "",
    funeralPlace: "",
    funeralDate: "",
    funeralTime: "",
    notes: "",
};

const TransportFormContext = React.createContext(null);

const Field = ({name, label, translation, full = false, multiline = false}) => {
    const {classes, fields, handleChange} = React.useContext(TransportFormContext);

    return (
        <label className={full ? classes.full : undefined}>
            <div className={classes.fieldLabel}>{label}</div>
            <div className={classes.fieldTranslation}>{translation}</div>
            {multiline ? (
                <textarea name={name} value={fields[name]} onChange={handleChange} className={`${classes.input} ${classes.textarea}`}/>
            ) : (
                <input name={name} value={fields[name]} onChange={handleChange} className={classes.input}/>
            )}
        </label>
    );
};

const Section = ({number, title, translation, children, threeColumns = false}) => {
    const {classes} = React.useContext(TransportFormContext);

    return (
        <section className={classes.section}>
            <div className={classes.sectionHeader}>
                <div className={classes.sectionTitle}>{number}. {title}</div>
                <div className={classes.sectionTranslation}>{translation}</div>
            </div>
            <div className={`${classes.sectionBody} ${threeColumns ? classes.threeColumns : ""}`}>{children}</div>
        </section>
    );
};

const InternationalTransportFormGenerator = (props) => {
    const classes = useStyles();
    const pdfRef = useRef(null);
    const funeralId = useParams();
    const userLocal = JSON.parse(localStorage.getItem("user")).id;
    const [funeral, setFuneral] = useState({_id: "", deceased: "", evidence: {}});
    const [user, setUser] = useState(null);
    const [fields, setFields] = useState(initialFields);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const handleLoadError = (error) => {
            if (error.response?.status === 401) {
                logOut(props);
                NotificationManager.warning("Potrebné sa znova prihlásiť!", "Upozornenie", 5000);
            }
        };

        UserService.getUser(userLocal).then((response) => {
            const currentUser = response.data;
            setUser(currentUser);
            setFields((current) => ({...current,
                companyName: currentUser.name || "",
                companyAddress: currentUser.address || "",
                companyIds: [currentUser.ico && `IČO: ${currentUser.ico}`, currentUser.ic_dph && `IČ DPH: ${currentUser.ic_dph}`].filter(Boolean).join(" / "),
                companyPhone: currentUser.phone || "",
                companyEmail: currentUser.email_official || currentUser.email || "",
            }));
        }, (error) => handleLoadError(error));

        UserService.getFuneral(funeralId.funeral_id).then((response) => {
            const currentFuneral = response.data;
            const evidence = currentFuneral.evidence || {};
            setFuneral(currentFuneral);
            setFields((current) => ({...current,
                manager: evidence.employee || "",
                driverOne: evidence.employee_transfer || "",
                vehiclePlate: evidence.car_id || "",
                deceased: currentFuneral.deceased || "",
                deceasedId: currentFuneral.d_id || "",
                birthDate: formatDate(currentFuneral.d_birth),
                birthPlace: currentFuneral.d_place_birth || "",
                deathDate: formatDate(currentFuneral.d_death),
                deathPlace: currentFuneral.d_place_death || "",
                institutionAddress: currentFuneral.planned_pickup_place || evidence.placement_of_decedent || "",
                funeralPlace: [currentFuneral.place_funeral, currentFuneral.town_funeral].filter(Boolean).join(", "),
                funeralDate: formatDate(currentFuneral.date_funeral),
                funeralTime: formatTime(currentFuneral.date_funeral),
                notes: currentFuneral.notes || "",
            }));
        }, (error) => handleLoadError(error));
    }, [funeralId.funeral_id, userLocal, props]);

    const handleChange = ({target: {name, value}}) => setFields((current) => ({...current, [name]: value}));
    const handleExport = () => savePdfWithEmbeddedImages(pdfRef, (error) => {
        if (error) NotificationManager.error("Nepodarilo sa exportovať PDF!", "Chyba", 5000);
    });

    const fileSuffix = (funeral.deceased || "zosnuly").replace(/\s/g, "");

    return (
        <Container maxWidth="lg" className={classes.seeMore}>
            <div className={classes.root}>
                <Paper className={classes.paper}>
                    <Grid container>
                        <Grid xs={12} sm={4} md={3} item container><Toolbar><Button variant="contained" color="secondary" onClick={() => setSuccess(true)}>Späť na detaily</Button></Toolbar></Grid>
                        <Grid xs={12} sm={8} md={9} item container justifyContent="flex-end">
                            <Toolbar className="document-actions-toolbar">
                                <ActionButton color="primary"><Tooltip title="Pdf export"><PictureAsPdfIcon fontSize="small" onClick={handleExport}/></Tooltip></ActionButton>
                                <GeneratedDocumentEmailAction pdfRef={pdfRef} funeral={funeral} user={user}/>
                            </Toolbar>
                        </Grid>
                    </Grid>
                    <Divider/>
                    <Grid className={classes.marginTop} container direction="column" alignItems="center">
                        <div className={classes.editHint}>Po KLIKNUTÍ viete dopisovať alebo meniť údaje.</div>
                        <div className="document-preview-frame">
                            <PDFExport ref={pdfRef} {...getPdfExportProxyProps()} paperSize="A4" scale={0.75} forcePageBreak=".international-page-break" fileName={`medzinarodny-prepravny-formular-${fileSuffix}`}>
                                <TransportFormContext.Provider value={{classes, fields, handleChange}}>
                                <div className={classes.pages}>
                                    <div className={classes.page}>
                                        <header className={classes.header}>
                                            {user?.logo ? <PrivateImage src={user.logo} className={classes.logo} alt="Logo pohrebnej služby"/> : <div className={classes.logoPlaceholder}>LOGO<br/>POHREBNEJ SLUŽBY</div>}
                                            <div><h1 className={classes.title}>MEDZINÁRODNÝ PREPRAVNÝ FORMULÁR ZOSNULÉHO</h1><div className={classes.languages}>EN: International Deceased Transport Form · DE: Internationales Formular für den Leichentransport<br/>PL: Międzynarodowy formularz transportu zwłok · CZ: Mezinárodní formulář pro přepravu zesnulého</div></div>
                                            <div className={classes.metaBox}>
                                                <label className={classes.metaRow}>Číslo / No:<input name="number" value={fields.number} onChange={handleChange} className={classes.input}/></label>
                                                <label className={classes.metaRow}>Dátum / Date:<input name="documentDate" value={fields.documentDate} onChange={handleChange} className={classes.input}/></label>
                                            </div>
                                        </header>
                                        <Section number="1" title="ÚDAJE POHREBNEJ SLUŽBY" translation="EN: Funeral Service Details | DE: Angaben des Bestattungsunternehmens | PL: Dane zakładu pogrzebowego | CZ: Údaje pohřební služby">
                                            <Field name="companyName" label="Názov spoločnosti" translation="EN: Company name | DE: Firmenname | PL: Nazwa firmy | CZ: Název společnosti"/>
                                            <Field name="manager" label="Zodpovedný vedúci" translation="EN: Responsible manager | DE: Verantwortlicher Leiter | PL: Kierownik odpowiedzialny | CZ: Odpovědný vedoucí"/>
                                            <Field name="companyAddress" label="Adresa sídla" translation="EN: Registered address | DE: Sitzadresse | PL: Adres siedziby | CZ: Adresa sídla"/>
                                            <Field name="companyIds" label="IČO / DIČ (Registrácia)" translation="EN: Reg. No. / Tax ID | DE: Reg.-Nr. / USt-IdNr. | PL: NIP / REGON | CZ: IČO / DIČ"/>
                                            <Field name="companyPhone" label="Telefonický kontakt" translation="EN: Phone contact | DE: Telefonkontakt | PL: Kontakt telefoniczny | CZ: Telefonní kontakt"/>
                                            <Field name="companyEmail" label="E-mail" translation="EN: E-mail address | DE: E-Mail-Adresse | PL: Adres e-mail | CZ: E-mail"/>
                                        </Section>
                                        <Section number="2" title="MENÁ ZAMESTNANCOV POHREBNEJ SLUŽBY" translation="EN: Names of Funeral Service Employees | DE: Namen der Mitarbeiter | PL: Imiona i nazwiska pracowników | CZ: Jména zaměstnanců">
                                            <Field name="driverOne" label="Vodič 1 (Meno a priezvisko)" translation="EN: Driver 1 (Full name) | DE: Fahrer 1 | PL: Kierowca 1 | CZ: Řidič 1"/>
                                            <Field name="driverTwo" label="Vodič 2 / Sprievodca (Meno a priezvisko)" translation="EN: Driver 2 / Accompanying person | DE: Fahrer 2 / Begleitperson | PL: Kierowca 2 / Towarzysz | CZ: Řidič 2 / Doprovod"/>
                                        </Section>
                                        <Section number="3" title="VOZIDLO PREPRAVUJÚCE ZOSNULÉHO" translation="EN: Vehicle Transporting the Deceased | DE: Fahrzeug für die Überführung | PL: Pojazd przewożący zmarłego | CZ: Vozidlo pro převoz zesnulého" threeColumns>
                                            <Field name="vehicleModel" label="Značka a model vozidla" translation="EN: Vehicle make & model | DE: Fahrzeugmarke & Modell | PL: Marka i model | CZ: Značka a model"/>
                                            <Field name="vehiclePlate" label="EČV (ŠPZ)" translation="EN: License plate | DE: Kennzeichen | PL: Numer rejestracyjny | CZ: SPZ"/>
                                            <Field name="vehicleCountry" label="Štát registrácie vozidla" translation="EN: Country of registration | DE: Zulassungsland | PL: Kraj rejestracji | CZ: Stát registrace"/>
                                        </Section>
                                        <Section number="4" title="ÚDAJE ZOSNULÉHO" translation="EN: Details of the Deceased | DE: Angaben des Verstorbenen | PL: Dane zmarłego | CZ: Údaje zesnulého">
                                            <Field name="deceased" label="Meno a priezvisko" translation="EN: First name and surname | DE: Vorname und Nachname | PL: Imię i nazwisko | CZ: Jméno a příjmení"/>
                                            <Field name="deceasedId" label="Rodné číslo / Identifikačné číslo" translation="EN: Personal ID | DE: Geburtsnummer | PL: PESEL | CZ: Rodné číslo"/>
                                            <Field name="birthDate" label="Dátum narodenia" translation="EN: Date of birth | DE: Geburtsdatum | PL: Data urodzenia | CZ: Datum narození"/>
                                            <Field name="birthPlace" label="Miesto narodenia" translation="EN: Place of birth | DE: Geburtsort | PL: Miejsce urodzenia | CZ: Místo narození"/>
                                            <Field name="deathDate" label="Dátum úmrtia" translation="EN: Date of death | DE: Sterbedatum | PL: Data śmierci | CZ: Datum úmrtí"/>
                                            <Field name="deathPlace" label="Miesto úmrtia" translation="EN: Place of death | DE: Sterbeort | PL: Miejsce śmierci | CZ: Místo úmrtí"/>
                                        </Section>
                                        <div className={classes.pageNumber}>Strana 1 z 2</div>
                                    </div>
                                    <div className={`${classes.page} ${classes.secondPage} international-page-break`}>
                                        <Section number="5" title="MIESTO, KDE SA ZOSNULÝ NACHÁDZA" translation="EN: Location of the Deceased | DE: Aufenthaltsort des Verstorbenen | PL: Miejsce, w którym znajduje się zmarły | CZ: Místo, kde se zesnulý nachází">
                                            <Field full name="institution" label="Názov inštitúcie / Nemocnice / Patológie" translation="EN: Name of institution / Hospital / Mortuary | DE: Name der Institution / Krankenhaus / Pathologie | PL: Nazwa instytucji / Szpitala / Kostnicy | CZ: Název instituce / Nemocnice / Patologie"/>
                                            <Field full name="institutionAddress" label="Presná adresa (Ulica, Mesto, Štát)" translation="EN: Full address | DE: Genaue Adresse | PL: Dokładny adres | CZ: Přesná adresa"/>
                                        </Section>
                                        <Section number="6" title="TRASA GOOGLE MAPS NA SLOVENSKO" translation="EN: Google Maps Route to Slovakia | DE: Google Maps Route nach Slowakei | PL: Trasa Google Maps na Słowację | CZ: Trasa Google Maps na Slovensko">
                                            <div className={`${classes.full} ${classes.routeGrid}`}>
                                                <div>
                                                    <Field full name="routeUrl" label="Odkaz na Google Maps trasu (URL)" translation="EN: Google Maps route link | DE: Google Maps Routenlink | PL: Link do trasy Google Maps | CZ: Odkaz na trasu Google Maps"/>
                                                    <Field full multiline name="routeNotes" label="Poznámky k trase (Hraničné priechody, obchádzky)" translation="EN: Route notes | DE: Routenhinweise | PL: Uwagi dotyczące trasy | CZ: Poznámky k trase"/>
                                                </div>
                                                <div><div className={classes.fieldLabel}>QR KÓD / QR CODE</div><div className={classes.qr}>VLOŽIŤ<br/>QR KÓD</div></div>
                                            </div>
                                        </Section>
                                        <Section number="7" title="KDE A KEDY BUDE ZOSNULÝ POCHOVANÝ" translation="EN: Where and When the Deceased Will Be Buried | DE: Ort und Zeit der Bestattung | PL: Gdzie i kiedy zmarły zostanie pochowany | CZ: Kde a kdy bude zesnulý pochován" threeColumns>
                                            <Field name="funeralPlace" label="Miesto pohrebu (Cintorín, Krematórium, Mesto)" translation="EN: Place of burial | DE: Bestattungsort | PL: Miejsce pochówku | CZ: Místo pohřbu"/>
                                            <Field name="funeralDate" label="Dátum pohrebu" translation="EN: Date of burial | DE: Bestattungsdatum | PL: Data pogrzebu | CZ: Datum pohřbu"/>
                                            <Field name="funeralTime" label="Čas pohrebu" translation="EN: Time of burial | DE: Uhrzeit | PL: Godzina pogrzebu | CZ: Čas pohřbu"/>
                                            <Field full multiline name="notes" label="Poznámky / Špecifické požiadavky" translation="EN: Notes / Specific requirements | DE: Anmerkungen | PL: Uwagi | CZ: Poznámky"/>
                                        </Section>
                                        <div className={classes.signatures}>
                                            <div className={classes.signature}><div className={classes.signatureLine}>Podpis zodpovednej osoby<br/><small>Signature of the responsible person</small></div></div>
                                            <div className={classes.signature}><div className={classes.signatureLine}>Podpis prijímajúcej osoby<br/><small>Signature of the receiving person</small></div></div>
                                        </div>
                                        <div className={classes.pageNumber}>Strana 2 z 2</div>
                                    </div>
                                </div>
                                </TransportFormContext.Provider>
                            </PDFExport>
                        </div>
                    </Grid>
                </Paper>
            </div>
            {success ? <Redirect to={{pathname: `/pohreb/${funeralId.funeral_id}/info`, state: {from: props.location, number: 5}}}/> : <></>}
        </Container>
    );
};

export default InternationalTransportFormGenerator;
